import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePrivy } from "@privy-io/react-auth";
import { useAuth } from "@/hooks/useAuth";
import { authApi } from "@/api/auth";
import { analytics } from "@/services/analytics";

const ProfileCreation = () => {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const { updateUser } = useAuth();
  const [username, setUsername] = useState("");
  const [isUsernameValid, setIsUsernameValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  // TODO: Re-enable when image upload is required again
  // const [hasAvatar, setHasAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxUsernameLength = 20;
  const minUsernameLength = 4;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow alphanumeric characters and underscores
    const sanitized = value.replace(/[^a-zA-Z0-9_]/g, "");

    if (sanitized.length <= maxUsernameLength) {
      setUsername(sanitized);
      // Validate username (4-20 characters, no special characters)
      setIsUsernameValid(
        sanitized.length >= minUsernameLength &&
          sanitized.length <= maxUsernameLength
      );
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      setError("Only JPEG, PNG, and WebP images are allowed");
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError("Maximum file size is 5MB");
      return;
    }

    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload immediately
    setIsUploadingAvatar(true);
    try {
      const updatedUser = await authApi.uploadAvatar(file);
      updateUser(updatedUser);
      // TODO: Re-enable when image upload is required again
      // setHasAvatar(true);
    } catch (error: any) {
      console.error("Failed to upload avatar:", error);
      setError(error.response?.data?.message || "Failed to upload avatar");
      setAvatarPreview(null);
      // TODO: Re-enable when image upload is required again
      // setHasAvatar(false);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleCompleteProfile = async () => {
    // TODO: Re-enable avatar requirement when image upload is ready
    // if (!isUsernameValid || !hasAvatar || isSubmitting) return;
    if (!isUsernameValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      // Call backend to update username
      const updatedUser = await authApi.updateProfile({ name: username });

      // Update context with new user data
      updateUser(updatedUser);

      // Track profile creation and onboarding completion
      analytics.track.profileCreated({
        hasAvatar: !!avatarPreview,
        usernameLength: username.length,
      });
      analytics.track.onboardingCompleted();

      // Navigate to success screen
      navigate("/profile-success");
    } catch (error: any) {
      console.error("Failed to create profile:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to save profile. Please try again.";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col p-4">
      {/* Sticky safe-area spacer */}
      <div className="sticky top-0 z-10 w-full h-[env(safe-area-inset-top)]" />

      <main className="flex flex-1 flex-col px-2">
        {/* Title and Subtitle */}
        <div className="pt-10 pb-8 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight text-zinc-900 dark:text-white">
            Create Your Profile
          </h1>
          <p className="text-base font-normal leading-normal text-zinc-600 dark:text-zinc-400 pt-2">
            This is how other challengers will see you.
          </p>
        </div>

        {/* Avatar Selection */}
        <div className="flex w-full flex-col items-center gap-4 py-6">
          <div className="relative">
            <div
              className="bg-center bg-no-repeat aspect-square bg-cover rounded-full h-32 w-32 bg-zinc-200 dark:bg-zinc-800"
              style={
                avatarPreview
                  ? { backgroundImage: `url(${avatarPreview})` }
                  : undefined
              }
              aria-label="Profile avatar placeholder"
            />
            <button
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar}
              className="absolute bottom-0 right-0 flex h-9 w-9 cursor-pointer items-center justify-center overflow-hidden rounded-full border-2 border-background-dark bg-primary text-white hover:scale-110 transition-transform disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Add profile picture"
            >
              {isUploadingAvatar ? (
                <span className="material-symbols-outlined text-xl animate-spin">
                  progress_activity
                </span>
              ) : (
                <span className="material-symbols-outlined text-xl">add</span>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
          </div>
        </div>

        {/* Form Fields */}
        <div className="flex w-full flex-col gap-5 py-4">
          {/* Email Field (Read-only) */}
          <div className="flex flex-col">
            <p className="pb-2 text-base font-medium text-zinc-900 dark:text-white">
              Email Address
            </p>
            <div className="flex h-14 w-full min-w-0 flex-1 items-center overflow-hidden rounded-lg bg-zinc-200/50 p-4 text-base font-normal leading-normal text-zinc-500 dark:bg-zinc-800/50 dark:text-zinc-400">
              {user?.email?.address || "No email provided"}
            </div>
          </div>

          {/* Username Field */}
          <div className="flex flex-col">
            <div className="flex items-baseline justify-between">
              <p className="pb-2 text-base font-medium text-zinc-900 dark:text-white">
                Username
              </p>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {username.length}/{maxUsernameLength}
              </p>
            </div>
            <div className="relative flex items-center">
              <input
                className="form-input flex h-14 w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg border-2 border-zinc-300 bg-transparent dark:bg-zinc-900 p-4 text-base font-normal leading-normal text-zinc-900 placeholder:text-zinc-500 focus:border-primary focus:outline-0 focus:ring-0 dark:border-zinc-700 dark:text-white dark:placeholder:text-zinc-400"
                placeholder="Enter a unique username"
                value={username}
                onChange={handleUsernameChange}
                maxLength={maxUsernameLength}
              />
              {isUsernameValid && (
                <span className="material-symbols-outlined absolute right-4 text-xl text-primary">
                  check_circle
                </span>
              )}
            </div>
            <p className="pt-2 text-sm font-normal text-zinc-600 dark:text-zinc-400">
              Must be {minUsernameLength}-{maxUsernameLength} characters. No
              special characters.
            </p>
            {error && (
              <p className="pt-2 text-sm font-normal text-red-500">{error}</p>
            )}
          </div>
        </div>
      </main>

      {/* Footer CTA */}
      <footer className="mt-auto w-full py-4">
        <button
          onClick={handleCompleteProfile}
          // TODO: Re-enable avatar requirement when image upload is ready
          // disabled={!isUsernameValid || !hasAvatar || isSubmitting}
          disabled={!isUsernameValid || isSubmitting}
          className="flex min-w-[84px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-14 w-full bg-primary text-base font-bold leading-normal tracking-wide text-black disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:text-zinc-500 dark:disabled:bg-zinc-800 dark:disabled:text-zinc-600 hover:opacity-90 transition-opacity"
        >
          <span className="truncate">
            {isSubmitting ? "Saving..." : "Complete Profile"}
          </span>
        </button>
      </footer>
    </div>
  );
};

export default ProfileCreation;
